import React from 'react';
import { useDebounce } from '@/hooks';
import { useAppSelector } from '@/state';
import {
  qualityControlDispositionEnums,
  QualityControlModule,
  qualityControlModuleEnums,
  qualityControlRootCauseCategoryEnums,
  QualityControlSeverity,
  qualityControlSeverityEnums,
} from '@/types/enums';
import { getFileNameWithoutExtension } from '@/utils/utilFunctions';
import {
  CoreApi,
  ProcurementApi,
  ProductionApi,
  ProjectsApi,
  QualityControlApi,
  ReleasesApi,
  UsersApi,
} from '@api';
import {
  Box,
  Button,
  Card,
  FileUploadFileMimeType,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  FormController,
  FormFileUpload,
  FormInput,
  FormTextArea,
} from '@components/form';
import { skipToken } from '@reduxjs/toolkit/query';
import { CoreType, QualityControlType } from '@types';
import { OverlayDialog, toasterApi } from '@ui';
import {
  Control,
  FieldErrors,
  Path,
  UseFormRegister,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';

interface Props {
  isNew?: boolean;
  event_id?: number;
  onClose?: () => void;
  defaultProjectId?: number;
  defaultReleaseId?: number;
  defaultModule?: string;
  defaultPartNumber?: string;
  /** Additional prefill context (used e.g. by production-floor stage completion). */
  defaultWorkOrderId?: number;
  defaultFrameTag?: string;
  defaultSeverity?: string;
  defaultDescription?: string;
  defaultQtyAffected?: number;
  defaultResult?: string;
  /** Called with the new event id after a successful create (in addition to onClose). */
  onSuccess?: (eventId?: number) => void;
  /** Override the "Save Draft" button label. */
  submitLabel?: string;
  /** Hide the "Close Event" button (e.g. when logging a deficiency from another flow). */
  hideCloseButton?: boolean;
}

type SelectOption = {
  label: string;
  value: string | number;
};

type NativeSelectFieldProps<T extends Record<string, any>> = {
  label: string;
  id: Path<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  options: SelectOption[];
  required?: boolean;
  placeholder?: string;
  valueAsNumber?: boolean;
};

const selectShellStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '42px',
  height: '42px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '6px',
  border: '1px solid var(--chakra-colors-border-emphasized, #A0AEC0)',
  background: 'var(--chakra-colors-bg, #FFFFFF)',
  color: 'var(--chakra-colors-fg, #1A202C)',
  boxSizing: 'border-box',
  overflow: 'hidden',
  boxShadow: 'inset 0 0 0 1px transparent',
};

const selectElementStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  minHeight: '42px',
  padding: '0 12px',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: 'inherit',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  fontSize: '14px',
  lineHeight: '20px',
  boxSizing: 'border-box',
};

const selectIconStyle: React.CSSProperties = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--chakra-colors-fg-muted, #4A5568)',
  fontSize: '14px',
  lineHeight: 1,
};

const nativeOptionStyle: React.CSSProperties = {
  background: 'var(--chakra-colors-bg, #FFFFFF)',
  color: 'var(--chakra-colors-fg, #1A202C)',
};

const NativeSelectField = <T extends Record<string, any>>({
  label,
  id,
  register,
  errors,
  options,
  required = false,
  placeholder = 'Select an option',
  valueAsNumber = false,
}: NativeSelectFieldProps<T>) => {
  const error = id
    .split('.')
    .reduce<any>((acc, key) => (acc ? acc[key] : undefined), errors as any);

  return (
    <Flex width="100%" minW={0} flexDirection="column">
      <Text mb={1.5} fontSize="sm" fontWeight="medium" color="fg">
        {label}
        {required ? ' *' : ''}
      </Text>

      <Box position="relative" width="100%">
        <Box
          style={{
            ...selectShellStyle,
            border: error
              ? '1px solid var(--chakra-colors-red-500, #E53E3E)'
              : '1px solid var(--chakra-colors-border-emphasized, #A0AEC0)',
            boxShadow: error
              ? '0 0 0 1px var(--chakra-colors-red-500, #E53E3E)'
              : 'inset 0 0 0 1px transparent',
          }}
        >
          <select
            {...register(id, {
              ...(valueAsNumber
                ? {
                    setValueAs: (value) =>
                      value === '' || value === undefined ? 0 : Number(value),
                  }
                : {}),
            })}
            style={selectElementStyle}
          >
            <option value="" style={nativeOptionStyle}>
              {placeholder}
            </option>
            {options.map((option) => (
              <option
                key={`${option.value}`}
                value={option.value}
                style={nativeOptionStyle}
              >
                {option.label}
              </option>
            ))}
          </select>
        </Box>

        <Box as="span" style={selectIconStyle}>
          ▼
        </Box>
      </Box>

      <Text color="red.500" fontSize="xs" mt={1} minH="16px">
        {error?.message ? String(error.message) : ' '}
      </Text>
    </Flex>
  );
};

const Inner_ManageCard: React.FC<Props> = ({
  isNew,
  event_id,
  onClose,
  defaultProjectId,
  defaultReleaseId,
  defaultModule,
  defaultPartNumber,
  defaultWorkOrderId,
  defaultFrameTag,
  defaultSeverity,
  defaultDescription,
  defaultQtyAffected,
  defaultResult,
  onSuccess,
  submitLabel,
  hideCloseButton,
}: Props) => {
  const formSchema = qualityControlEventsSchema;
  type FormData = z.infer<typeof formSchema>;

  const [postEventsMutation, { isSuccess: isPostEventsMutationSuccess }] =
    QualityControlApi.Api.usePostEventsMutation();
  const [
    patchEventByIdMutation,
    { isSuccess: isPatchEventByIdMutationSuccess },
  ] = QualityControlApi.Api.usePatchEventByIdMutation();
  const [
    patchEventAttachmentByIdMutation,
    { isSuccess: isPatchEventAttachmentByIdMutationSuccess },
  ] = QualityControlApi.Api.usePatchEventAttachmentByIdMutation();

  const { data: getAllProjects } = ProjectsApi.Api.useGetAllProjectsQuery();
  const { data: getAllReleases } = ReleasesApi.Api.useGetAllReleaseQuery();
  const { data: getAllUserNames } = UsersApi.Api.useGetAllUserNamesQuery();

  const [postCreateCloseEventMutation] =
    QualityControlApi.Api.usePostCreateCloseEventMutation();
  const [patchUpdateCloseEventMutation] =
    QualityControlApi.Api.usePatchUpdateCloseEventMutation();
  const [postAttachmentsPresignedUrlsMutation] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const { data: currentEvent } = QualityControlApi.Api.useGetEventByIdQuery(
    !isNew && event_id !== undefined && event_id > 0
      ? {
          params: {
            id: event_id,
          },
        }
      : skipToken,
  );

  const onFormSubmit = async (formData: FormData) => {
    if (formData?.submissionType === 'createOrUpdateEvent') {
      OverlayDialog.open('confirmQualityControlEventUpdate', {
        overlayId: 'confirmQualityControlEventUpdate',
        title: `Confirm ${isNew ? 'Quality Control Event Creation' : 'Quality Control Event Update'}`,
        description: `Are you sure you want to ${
          isNew ? 'create' : 'update'
        } the quality control event?`,
        handleConfirm: async () => {
          const payload = {
            project: formData.schema.project,
            release: formData.schema.release,
            module: formData.schema.module as QualityControlModule,
            severity: formData.schema.severity as QualityControlSeverity,
            description: formData.schema.description.trim(),
            qty_affected: formData.schema.qty_affected,
            ...(formData.schema.result && {
              result: formData.schema.result,
            }),
            ...(formData.schema.corrective_action?.trim() && {
              corrective_action: formData.schema.corrective_action.trim(),
            }),
            ...(formData.schema.disposition && {
              disposition: formData.schema.disposition,
            }),
            ...(formData.schema.due_date && {
              due_date: formData.schema.due_date,
            }),
            ...(formData.schema.immediate_action?.trim() && {
              immediate_action: formData.schema.immediate_action.trim(),
            }),
            ...(formData.schema.location?.trim() && {
              location: formData.schema.location.trim(),
            }),
            ...(formData.schema.part_number?.trim() && {
              part_number: formData.schema.part_number.trim(),
            }),
            ...(formData.schema.preventive_action?.trim() && {
              preventive_action: formData.schema.preventive_action.trim(),
            }),
            ...(formData.schema.root_cause?.trim() && {
              root_cause: formData.schema.root_cause.trim(),
            }),
            ...(formData.schema.root_cause_category && {
              root_cause_category: formData.schema.root_cause_category,
            }),
            ...(formData.schema.spec_ref?.trim() && {
              spec_ref: formData.schema.spec_ref.trim(),
            }),
            ...(formData.schema.detected_at && {
              detected_at: formData.schema.detected_at,
            }),
            ...(formData.schema.detected_by &&
              formData.schema.detected_by !== 0 && {
                detected_by: formData.schema.detected_by,
              }),
            ...(formData.schema.frame_tag?.trim() && {
              frame_tag: formData.schema.frame_tag.trim(),
            }),
            ...(formData.schema.owner &&
              formData.schema.owner !== 0 && {
                owner: formData.schema.owner,
              }),
            ...(formData.schema.purchase_order &&
              formData.schema.purchase_order !== 0 && {
                purchase_order: formData.schema.purchase_order,
              }),
            ...(formData.schema.work_order &&
              formData.schema.work_order !== 0 && {
                work_order: formData.schema.work_order,
              }),
          };

          let responseData: any = null;

          if (isNew) {
            const res = await postEventsMutation({
              body: payload,
            });

            const eventId = res?.data?.id;

            if (eventId && formData?.schema?.attachments?.length) {
              await patchEventAttachmentByIdMutation({
                params: { id: eventId },
                body: formData.schema.attachments as File[],
              });
            }

            responseData = res?.data;
          } else {
            const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
              (formData.schema.attachments ?? []).map((file) => ({
                module: 'quality_control',
                filename: getFileNameWithoutExtension(file.name),
                content_type: file.type,
                size: file.size,
              }));

            const getPresignedUrls = await postAttachmentsPresignedUrlsMutation(
              {
                body: getPresignedUrlsPayload,
              },
            );

            const uploadResponse = getPresignedUrls?.data?.map(async (item) => {
              const file = formData.schema.attachments?.find(
                (f) => getFileNameWithoutExtension(f.name) === item.filename,
              );
              if (!item || !file) return null;

              await fetch(item.upload_url, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file,
              });

              return {
                filename: item.filename,
                file: item.file_url,
              };
            });

            const uploadResults = await Promise.allSettled(
              uploadResponse ?? [],
            );

            const successfullyUploadedFiles = uploadResults
              .filter(
                (
                  r,
                ): r is PromiseFulfilledResult<{
                  filename: string;
                  file: string;
                } | null> => r.status === 'fulfilled',
              )
              .map((r) => r.value)
              .filter(Boolean);

            const res = await patchEventByIdMutation({
              params: { id: event_id as number },
              body: {
                ...payload,
                ...(successfullyUploadedFiles.length && {
                  attachments: successfullyUploadedFiles.map((f) => ({
                    file_url: f!.file,
                    filename: f!.filename,
                  })),
                }),
              },
            });

            responseData = res?.data;
          }

          if (responseData) {
            toasterApi({
              type: 'success',
              status: isNew ? 201 : 200,
              code: 'Success',
              message: `${isNew ? 'Created' : 'Updated'} Quality Control Event`,
            });
            if (isNew) {
              onSuccess?.(responseData?.id);
            }
            onClose?.();
          }
        },
      });

      return;
    }

    if (formData?.submissionType === 'closeEvent') {
      OverlayDialog.open('closeEvent', {
        overlayId: 'closeEvent',
        title: 'Close QC Event',
        description:
          'Record the closure details for this quality event. These notes are kept as audit data and shown after the event is closed. Either Part Number or Frame Tag is required.',
        content: (
          <FormController
            key={`quality-control-close-event-form-${isPostEventsMutationSuccess}-${isPatchEventAttachmentByIdMutationSuccess}-${formData?.schema?.project}`}
            label=""
            formSchema={closeEventSchema}
            defaultValues={{
              immediate_action: formData.schema.immediate_action ?? '',
              frame_tag: formData.schema.frame_tag ?? '',
              part_number: formData.schema.part_number ?? '',
              corrective_action: formData.schema.corrective_action ?? '',
              preventive_action: formData.schema.preventive_action ?? '',
              closure_notes: '',
            }}
            onFormSubmit={async (closeEventFormData) => {
              const optionalPayloadData = {
                ...(formData.schema.disposition && {
                  disposition: formData.schema.disposition,
                }),
                ...(formData.schema.due_date && {
                  due_date: formData.schema.due_date,
                }),
                ...(formData.schema.location?.trim() && {
                  location: formData.schema.location.trim(),
                }),
                ...(closeEventFormData.part_number?.trim() && {
                  part_number: closeEventFormData.part_number.trim(),
                }),
                ...(formData.schema.root_cause?.trim() && {
                  root_cause: formData.schema.root_cause.trim(),
                }),
                ...(formData.schema.root_cause_category && {
                  root_cause_category: formData.schema.root_cause_category,
                }),
                ...(formData.schema.spec_ref?.trim() && {
                  spec_ref: formData.schema.spec_ref.trim(),
                }),
                ...(formData.schema.detected_at && {
                  detected_at: formData.schema.detected_at,
                }),
                ...(formData.schema.detected_by &&
                  formData.schema.detected_by !== 0 && {
                    detected_by: formData.schema.detected_by,
                  }),
                ...(formData.schema.owner &&
                  formData.schema.owner !== 0 && {
                    owner: formData.schema.owner,
                  }),
                ...(formData.schema.purchase_order &&
                  formData.schema.purchase_order !== 0 && {
                    purchase_order: formData.schema.purchase_order,
                  }),
                ...(formData.schema.work_order &&
                  formData.schema.work_order !== 0 && {
                    work_order: formData.schema.work_order,
                  }),
              };

              const requiredPayloadData = {
                project: formData.schema.project,
                release: formData.schema.release,
                module: formData.schema.module as QualityControlModule,
                severity: formData.schema.severity as QualityControlSeverity,
                description: formData.schema.description.trim(),
                qty_affected: formData.schema.qty_affected,
                immediate_action: closeEventFormData.immediate_action.trim(),
                corrective_action: closeEventFormData.corrective_action.trim(),
                preventive_action: closeEventFormData.preventive_action.trim(),
                closure_notes: closeEventFormData.closure_notes.trim(),
                ...(closeEventFormData.frame_tag?.trim() && {
                  frame_tag: closeEventFormData.frame_tag.trim(),
                }),
              };

              const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
                (formData.schema.attachments ?? []).map((file) => ({
                  module: 'quality_control',
                  filename: file.name,
                  content_type: file.type,
                  size: file.size,
                }));

              const getPresignedUrls =
                await postAttachmentsPresignedUrlsMutation({
                  body: getPresignedUrlsPayload,
                });

              const uploadResponse = getPresignedUrls?.data?.map(
                async (item) => {
                  const file = formData.schema.attachments?.find(
                    (f) => f.name === item.filename,
                  );
                  if (!item || !file) return null;

                  await fetch(item.upload_url, {
                    method: 'PUT',
                    headers: { 'Content-Type': file.type },
                    body: file,
                  });

                  return {
                    filename: item.filename,
                    file: item.file_url,
                  };
                },
              );

              const uploadResults = await Promise.allSettled(
                uploadResponse ?? [],
              );

              const successfullyUploadedFiles = uploadResults
                .filter(
                  (
                    r,
                  ): r is PromiseFulfilledResult<{
                    filename: string;
                    file: string;
                  } | null> => r.status === 'fulfilled',
                )
                .map((r) => r.value)
                .filter(Boolean);

              const payload = {
                ...requiredPayloadData,
                ...optionalPayloadData,
                ...(successfullyUploadedFiles.length && {
                  attachments: successfullyUploadedFiles.map((f) => ({
                    file_url: f!.file,
                    filename: f!.filename,
                  })),
                }),
              };

              const responseData = isNew
                ? await postCreateCloseEventMutation({
                    body: payload,
                  }).unwrap()
                : await patchUpdateCloseEventMutation({
                    params: { id: event_id as number },
                    body: payload,
                  }).unwrap();

              if (responseData) {
                toasterApi({
                  type: 'success',
                  status: 201,
                  code: 'Success',
                  message: 'Successfully Closed Event',
                });
              }

              OverlayDialog.close('closeEvent');
              onClose?.();
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<CloseEventType>;
              control: Control<CloseEventType>;
              errors: FieldErrors<CloseEventType>;
            }) => (
              <Flex flexDirection="column" gapY={3}>
                <Text fontSize="xs" color="fg.muted">
                  Fields marked * are required.
                </Text>
                <Flex gapX={4}>
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Immediate Action"
                    id="immediate_action"
                    required
                    showLabelAtTop
                  />
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Corrective Action"
                    id="corrective_action"
                    required
                    showLabelAtTop
                  />
                </Flex>

                <Flex gapX={4}>
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Preventive Action"
                    id="preventive_action"
                    required
                    showLabelAtTop
                  />
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Frame Tag"
                    id="frame_tag"
                    showLabelAtTop
                  />
                </Flex>

                <Flex gapX={4}>
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Part Number"
                    id="part_number"
                    showLabelAtTop
                  />
                  <Box flex="1" />
                </Flex>

                {(errors.frame_tag?.message || errors.part_number?.message) && (
                  <Text color="red.500" fontSize="sm" mt={1} mb={2}>
                    {String(
                      errors.frame_tag?.message || errors.part_number?.message,
                    )}
                  </Text>
                )}

                <Text fontSize="xs" color="fg.muted" mt={1} mb={2}>
                  Either Part Number or Frame Tag must be provided.
                </Text>

                <FormTextArea
                  register={register}
                  control={control}
                  errors={errors}
                  label="Closure Notes"
                  id="closure_notes"
                  required
                  showLabelAtTop
                  minH="5lh"
                  maxH="5lh"
                />
              </Flex>
            )}
          </FormController>
        ),
      });
    }
  };

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );

  return (
    <Card.Root width="100%" variant="elevated" bg="transparent" border="none">
      <Card.Body gap="6" bg="transparent">
        <FormController
          key={`quality-control-event-form-${isNew}-${isPostEventsMutationSuccess}-${isPatchEventByIdMutationSuccess}-${isPatchEventAttachmentByIdMutationSuccess}-${currentEvent?.id}-${defaultProjectId}-${defaultReleaseId}-${defaultModule}-${defaultPartNumber}-${defaultWorkOrderId}-${defaultFrameTag}-${defaultResult}`}
          label={
            isNew
              ? 'Create Quality Control Event'
              : 'Update Quality Control Event Details'
          }
          helperText={`Please fill out the form to ${
            isNew
              ? 'create a new quality control event'
              : 'update the quality control event details'
          }.`}
          formSchema={formSchema}
          onFormSubmit={onFormSubmit}
          defaultValues={
            isNew
              ? {
                  submissionType: 'createOrUpdateEvent',
                  schema: {
                    attachments: [],
                    corrective_action: '',
                    disposition: '',
                    due_date: '',
                    immediate_action: '',
                    location: '',
                    module: defaultModule ?? '',
                    part_number: defaultPartNumber ?? '',
                    preventive_action: '',
                    root_cause: '',
                    root_cause_category: '',
                    spec_ref: '',
                    description: defaultDescription ?? '',
                    detected_at: '',
                    detected_by: 0,
                    qty_affected: defaultQtyAffected ?? 1,
                    severity: defaultSeverity ?? '',
                    result: defaultResult ?? 'INFO',
                    frame_tag: defaultFrameTag ?? '',
                    owner: 0,
                    project: defaultProjectId ?? selectedProject?.id ?? 0,
                    purchase_order: 0,
                    release: defaultReleaseId ?? selectedRelease?.id ?? 0,
                    work_order: defaultWorkOrderId ?? 0,
                  },
                }
              : {
                  submissionType: 'createOrUpdateEvent',
                  schema: {
                    attachments: [],
                    corrective_action: currentEvent?.corrective_action ?? '',
                    disposition: currentEvent?.disposition ?? '',
                    due_date: currentEvent?.due_date ?? '',
                    immediate_action: currentEvent?.immediate_action ?? '',
                    location: currentEvent?.location ?? '',
                    module: currentEvent?.module ?? '',
                    part_number: currentEvent?.part_number ?? '',
                    preventive_action: currentEvent?.preventive_action ?? '',
                    root_cause: currentEvent?.root_cause ?? '',
                    root_cause_category:
                      currentEvent?.root_cause_category ?? '',
                    spec_ref: currentEvent?.spec_ref ?? '',
                    description: currentEvent?.description ?? '',
                    detected_at: currentEvent?.detected_at ?? '',
                    detected_by: currentEvent?.detected_by ?? 0,
                    qty_affected: currentEvent?.qty_affected ?? 1,
                    severity: currentEvent?.severity ?? '',
                    result: currentEvent?.result ?? 'INFO',
                    frame_tag: currentEvent?.frame_tag ?? '',
                    owner: currentEvent?.owner ?? 0,
                    project: currentEvent?.project ?? 0,
                    purchase_order: currentEvent?.purchase_order ?? 0,
                    release: currentEvent?.release ?? 0,
                    work_order: currentEvent?.work_order ?? 0,
                  },
                }
          }
          buttonProps={{ hidden: true }}
        >
          {({
            register,
            control,
            errors,
            setValue,
            trigger,
          }: {
            register: UseFormRegister<FormData>;
            control: Control<FormData>;
            errors: FieldErrors<FormData>;
            setValue?: UseFormReturn<FormData>['setValue'];
            trigger?: UseFormReturn<FormData>['trigger'];
          }) => {
            const watchProject = useWatch({
              control,
              name: 'schema.project',
            });

            const watchRelease = useWatch({
              control,
              name: 'schema.release',
            });

            const watchPartNumber = useWatch({
              control,
              name: 'schema.part_number',
            });
            const previousPoScope = React.useRef({
              project: watchProject,
              partNumber: watchPartNumber,
            });

            React.useEffect(() => {
              const previous = previousPoScope.current;
              if (
                previous.project !== watchProject ||
                previous.partNumber !== watchPartNumber
              ) {
                setValue?.('schema.purchase_order', 0, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
              previousPoScope.current = {
                project: watchProject,
                partNumber: watchPartNumber,
              };
            }, [setValue, watchPartNumber, watchProject]);

            const debouncedWatchPartNumber = useDebounce(watchPartNumber, 400);

            const projectId = watchProject || selectedProject?.id;
            const releaseId = watchRelease || selectedRelease?.id;

            const { data: getAllWorkOrders } =
              ProductionApi.Api.useGetAllWorkOrdersQuery(
                typeof projectId === 'number' && typeof releaseId === 'number'
                  ? {
                      params: {
                        project_id: projectId,
                        release_id: releaseId,
                      },
                    }
                  : skipToken,
              );

            const { data: getPurchaseOrdersByPart } =
              ProcurementApi.Api.useGetPurchaseOrdersByPartQuery(
                debouncedWatchPartNumber && typeof projectId === 'number'
                  ? {
                      query: {
                        part_number: debouncedWatchPartNumber,
                        project_id: projectId,
                      },
                    }
                  : skipToken,
              );

            const projectOptions: SelectOption[] = (getAllProjects ?? []).map(
              (item) => ({
                label: String(item?.name ?? ''),
                value: item.id ?? 0,
              }),
            );

            const releaseOptions: SelectOption[] = (getAllReleases ?? []).map(
              (item) => ({
                label: String(item?.number ?? ''),
                value: item.id ?? 0,
              }),
            );

            const moduleOptions: SelectOption[] = (
              qualityControlModuleEnums ?? []
            ).map((item) => ({
              label: String(item?.label ?? ''),
              value: String(item?.value ?? ''),
            }));

            const severityOptions: SelectOption[] = (
              qualityControlSeverityEnums ?? []
            ).map((item) => ({
              label: String(item?.label ?? ''),
              value: String(item?.value ?? ''),
            }));

            const dispositionOptions: SelectOption[] = (
              qualityControlDispositionEnums ?? []
            ).map((item) => ({
              label: String(item?.label ?? ''),
              value: String(item?.value ?? ''),
            }));

            const rootCauseCategoryOptions: SelectOption[] = (
              qualityControlRootCauseCategoryEnums ?? []
            ).map((item) => ({
              label: String(item?.label ?? ''),
              value: String(item?.value ?? ''),
            }));

            const detectedByOptions: SelectOption[] = (
              getAllUserNames ?? []
            ).map((item) => ({
              label: String(item?.display_name ?? ''),
              value: item.id ?? 0,
            }));

            const purchaseOrderOptions: SelectOption[] = (
              debouncedWatchPartNumber ? (getPurchaseOrdersByPart ?? []) : []
            ).map((item) => ({
              label: [
                item?.project_po_number ?? item?.po_number,
                item?.title,
                item?.project_po_number && item?.po_number
                  ? `Reference: ${item.po_number}`
                  : null,
                item?.supplier_name,
              ]
                .filter(Boolean)
                .join(' — '),
              value: item.id ?? 0,
            }));

            const workOrderOptions: SelectOption[] = (
              getAllWorkOrders ?? []
            ).map((item) => ({
              label: String(item?.work_order_identifier ?? ''),
              value: item.id ?? 0,
            }));

            const submitFormWithType = async (
              event: React.MouseEvent<HTMLButtonElement>,
              submissionType: 'createOrUpdateEvent' | 'closeEvent',
            ) => {
              event.preventDefault();

              const form = event.currentTarget.form;

              if (!setValue || !trigger || !form) {
                console.error('Unable to submit form');
                return;
              }

              setValue('submissionType', submissionType, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
              });

              const isValid = await trigger('schema');

              if (!isValid) {
                console.error('Form validation failed', errors);
                return;
              }

              form.requestSubmit();
            };

            return (
              <Box width="100%">
                <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={4}>
                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Project"
                      id="schema.project"
                      register={register}
                      errors={errors}
                      options={projectOptions}
                      required
                      placeholder="Select project"
                      valueAsNumber
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Release"
                      id="schema.release"
                      register={register}
                      errors={errors}
                      options={releaseOptions}
                      required
                      placeholder="Select release"
                      valueAsNumber
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Module"
                      id="schema.module"
                      register={register}
                      errors={errors}
                      options={moduleOptions}
                      required
                      placeholder="Select module"
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Severity"
                      id="schema.severity"
                      register={register}
                      errors={errors}
                      options={severityOptions}
                      required
                      placeholder="Select severity"
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Affected Quantity"
                      id="schema.qty_affected"
                      type="number"
                      required
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Detected At"
                      id="schema.detected_at"
                      type="datetime-local"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem colSpan={3}>
                    <FormTextArea
                      register={register}
                      control={control}
                      errors={errors}
                      label="Description"
                      id="schema.description"
                      required
                      showLabelAtTop
                      minH="5lh"
                      maxH="5lh"
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Disposition"
                      id="schema.disposition"
                      register={register}
                      errors={errors}
                      options={dispositionOptions}
                      placeholder="Select disposition"
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Detected By"
                      id="schema.detected_by"
                      register={register}
                      errors={errors}
                      options={detectedByOptions}
                      placeholder="Select user"
                      valueAsNumber
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Part Number"
                      id="schema.part_number"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Purchase Order"
                      id="schema.purchase_order"
                      register={register}
                      errors={errors}
                      options={purchaseOrderOptions}
                      placeholder="Select purchase order"
                      valueAsNumber
                    />
                  </GridItem>

                  <GridItem>
                    <NativeSelectField<FormData>
                      label="Work Order"
                      id="schema.work_order"
                      register={register}
                      errors={errors}
                      options={workOrderOptions}
                      placeholder="Select work order"
                      valueAsNumber
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Immediate Action"
                      id="schema.immediate_action"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Corrective Action"
                      id="schema.corrective_action"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Preventive Action"
                      id="schema.preventive_action"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Frame Tag"
                      id="schema.frame_tag"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Due Date"
                      id="schema.due_date"
                      type="date"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Specification Reference"
                      id="schema.spec_ref"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem>
                    <FormInput
                      register={register}
                      control={control}
                      errors={errors}
                      label="Root Cause"
                      id="schema.root_cause"
                      showLabelAtTop
                    />
                  </GridItem>

                  <GridItem colSpan={2}>
                    <NativeSelectField<FormData>
                      label="Root Cause Category"
                      id="schema.root_cause_category"
                      register={register}
                      errors={errors}
                      options={rootCauseCategoryOptions}
                      placeholder="Select root cause category"
                    />
                  </GridItem>

                  {(errors.schema?.frame_tag?.message ||
                    errors.schema?.part_number?.message) && (
                    <GridItem colSpan={3}>
                      <Text color="red.500" fontSize="sm" mt={-2}>
                        {String(
                          errors.schema?.frame_tag?.message ||
                            errors.schema?.part_number?.message,
                        )}
                      </Text>
                    </GridItem>
                  )}

                  <GridItem colSpan={3}>
                    <Text fontSize="xs" color="fg.muted" mt={-2}>
                      Either Part Number or Frame Tag must be provided.
                    </Text>
                  </GridItem>

                  <GridItem colSpan={3}>
                    <Flex flexDirection="column">
                      <Text fontSize="sm" color="fg.muted" mb={2}>
                        Upload Attachment Files
                      </Text>
                      <FormFileUpload
                        control={control}
                        register={register}
                        errors={errors}
                        id="schema.attachments"
                        label=""
                        maxFiles={10}
                        accept={
                          [
                            'application/pdf',
                            'application/msword',
                            'image/*',
                          ] as FileUploadFileMimeType[]
                        }
                      />
                    </Flex>
                  </GridItem>

                  <GridItem colSpan={3}>
                    <Flex
                      justifyContent={
                        hideCloseButton ? 'flex-end' : 'space-between'
                      }
                      mt={2}
                    >
                      <Button
                        type="button"
                        onClick={(event) =>
                          submitFormWithType(event, 'createOrUpdateEvent')
                        }
                        variant={hideCloseButton ? 'solid' : 'outline'}
                        colorPalette={hideCloseButton ? 'red' : undefined}
                        color={hideCloseButton ? undefined : 'fg'}
                        borderColor={
                          hideCloseButton ? undefined : 'border.emphasized'
                        }
                      >
                        {submitLabel ?? 'Save Draft'}
                      </Button>

                      {!hideCloseButton && (
                        <Button
                          type="button"
                          onClick={(event) =>
                            submitFormWithType(event, 'closeEvent')
                          }
                        >
                          Close Event
                        </Button>
                      )}
                    </Flex>
                  </GridItem>
                </Grid>
              </Box>
            );
          }}
        </FormController>
      </Card.Body>
    </Card.Root>
  );
};

const ManageCard: React.FC<Props> = ({
  isNew = false,
  event_id,
  onClose,
  defaultProjectId,
  defaultReleaseId,
  defaultModule,
  defaultPartNumber,
  defaultWorkOrderId,
  defaultFrameTag,
  defaultSeverity,
  defaultDescription,
  defaultQtyAffected,
  defaultResult,
  onSuccess,
  submitLabel,
  hideCloseButton,
}: Props) => {
  return (
    <Stack width="100%">
      <Inner_ManageCard
        isNew={isNew}
        event_id={event_id}
        onClose={onClose}
        defaultProjectId={defaultProjectId}
        defaultReleaseId={defaultReleaseId}
        defaultModule={defaultModule}
        defaultPartNumber={defaultPartNumber}
        defaultWorkOrderId={defaultWorkOrderId}
        defaultFrameTag={defaultFrameTag}
        defaultSeverity={defaultSeverity}
        defaultDescription={defaultDescription}
        defaultQtyAffected={defaultQtyAffected}
        defaultResult={defaultResult}
        onSuccess={onSuccess}
        submitLabel={submitLabel}
        hideCloseButton={hideCloseButton}
      />
    </Stack>
  );
};

export default ManageCard;

const baseSchema = QualityControlType.postEvents.body;

const qualityControlEventsSchema = z.object({
  submissionType: z
    .enum(['createOrUpdateEvent', 'closeEvent'])
    .default('createOrUpdateEvent'),
  schema: baseSchema
    .omit({
      attachments: true,
    })
    .extend({
      description: z
        .string()
        .trim()
        .min(1, { message: 'Description is required' }),
      attachments: z.array(z.instanceof(File)).optional(),
      part_number: z.string().optional().default(''),
      frame_tag: z.string().optional().default(''),
    })
    .superRefine((data, ctx) => {
      const hasPartNumber = !!data.part_number?.trim();
      const hasFrameTag = !!data.frame_tag?.trim();

      if (!hasPartNumber && !hasFrameTag) {
        const message = 'Either Part Number or Frame Tag is required';

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['part_number'],
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['frame_tag'],
        });
      }
    }),
});

const closeEventSchema = QualityControlType.closeEventBaseSchema
  .extend({
    frame_tag: z.string().optional().default(''),
    part_number: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    const hasPartNumber = !!data.part_number?.trim();
    const hasFrameTag = !!data.frame_tag?.trim();

    if (!hasPartNumber && !hasFrameTag) {
      const message = 'Either Part Number or Frame Tag is required';

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['part_number'],
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['frame_tag'],
      });
    }
  });

type CloseEventType = z.infer<typeof closeEventSchema>;
